import APIBase from "./apiBase";
import { me, User } from "./userLib";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

type IAuthContext = {
  user?: User;
  loading: boolean;
  isAdmin: boolean;
};
// Create context w/ types and default arguments that throw errors
const AuthContext = createContext<IAuthContext>({
  loading: true,
  isAdmin: false,
});

interface AuthProviderProps {
  children: ReactNode;
}
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User>();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    /** On load lookup any stored auth if present */
    const lookupStoredAuth = async () => {
      try {
        const u = await me();
        if (u) {
          setUser(u);
          setIsAdmin(u.isSuperAdmin);
        }
      } catch (e) {
        console.log("Couldn't pull a user session", e);
      }
      setLoading(false);
    };

    lookupStoredAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const logout = async () => {
  const { data } = await APIBase.post<{
    url: string;
  }>(`/auth/logout`);
  console.log(data);
  return data;
};
export const launchGoogleAuth = async (loginHint?: string) => {
  try {
    const params: any = {
      redirect_uri: process.env.REACT_APP_DASHBOARD_OAUTH_CALLBACK_URI,
    };

    // Add login_hint if provided to pre-select user's account
    if (loginHint) {
      params.login_hint = loginHint;
    }

    const { data } = await APIBase.get<{
      url: string;
    }>(`/auth/google`, {
      params,
    });
    console.log(data);
    return data;
  } catch (error) {
    console.error("Failed to launch Google auth:", error);
    // Check if it's a network/API unreachable error
    if (error instanceof Error) {
      if (
        error.message.includes("Network Error") ||
        error.message.includes("Failed to fetch") ||
        error.message.includes("ERR_NETWORK") ||
        error.message.includes("ERR_CONNECTION_REFUSED")
      ) {
        throw new Error(
          "We couldn't connect to the Cobutler API, please try again later."
        );
      }
    }
    // Re-throw the original error for other types of failures
    throw error;
  }
};

interface AuthResponse {
  result: string;
  session: {
    token: string;
    expiry: number;
  };
}

// function getToken(): string | false {
//     const token = localStorage.getItem('cobutlerToken');
//     const expiry = localStorage.getItem('cobutlerTokenExpiry');
//
//     if (!token || !expiry) {
//         return false;
//     }
//
//     const expiryTime = parseInt(expiry, 10);
//
//     if (Date.now() > expiryTime) {
//         // If the token has expired, remove from localStorage and return false
//         localStorage.removeItem('cobutlerToken');
//         localStorage.removeItem('cobutlerTokenExpiry');
//         return false;
//     }
//
//     return token;
// }
export const loginEmailPassword = async (email: string, password: string) => {
  console.log(`Logging in with email`);
  const { data } = await APIBase.post<AuthResponse>(`/auth/login`, {
    email: email,
    password: password,
    appName: "dashboard",
  });
  localStorage.setItem("cobutlerToken", data.session.token);
  localStorage.setItem(
    "cobutlerTokenExpiry",
    (Date.now() + data.session.expiry * 1000).toString()
  );
};

// Keep track of in-flight requests
let pendingAuthRequest: Promise<AuthResponse | void> | null = null;

export const consumeGoogleAuthCode = async (code: string) => {
  // Check if we've already successfully processed this code
  const processedCode = sessionStorage.getItem("processedOAuthCode");
  if (processedCode === code) {
    console.log("Skipping duplicate OAuth code processing - already processed");
    return;
  }

  // If there's a pending request for this code, return that instead of starting a new one
  if (pendingAuthRequest) {
    console.log("Reusing pending auth request");
    return pendingAuthRequest;
  }

  console.log(`Sending code ${code}`);
  pendingAuthRequest = (async () => {
    try {
      const { data } = await APIBase.get<AuthResponse>(
        `/auth/google/callback`,
        {
          params: {
            code: code,
            redirect_uri: process.env.REACT_APP_DASHBOARD_OAUTH_CALLBACK_URI,
            appName: "dashboard",
          },
        }
      );
      console.log(`Got auth back, saving data to localstorage`, data);

      // Store the token first
      localStorage.setItem("cobutlerToken", data.session.token);
      localStorage.setItem(
        "cobutlerTokenExpiry",
        (Date.now() + data.session.expiry * 1000).toString()
      );

      // Mark this code as processed last - this ensures we don't mark it as processed
      // until we've successfully saved the token
      sessionStorage.setItem("processedOAuthCode", code);

      // Return the data so the caller knows it succeeded
      return data;
    } catch (error) {
      // Only throw if this wasn't already processed
      if (!sessionStorage.getItem("processedOAuthCode")) {
        throw error;
      }
    } finally {
      pendingAuthRequest = null;
    }
  })();

  return pendingAuthRequest;
};

export interface ReauthResult {
  inboxId: string;
  email: string;
}

export const launchGoogleAuthPopup = (
  loginHint: string,
  intent: "reauth" | "login" = "reauth"
): Promise<ReauthResult> => {
  return new Promise(async (resolve, reject) => {
    try {
      const params: any = {
        redirect_uri: process.env.REACT_APP_DASHBOARD_OAUTH_CALLBACK_URI,
        intent,
      };
      if (loginHint) {
        params.login_hint = loginHint;
      }

      const { data } = await APIBase.get<{ url: string }>(`/auth/google`, {
        params,
      });

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        data.url,
        "cobutler-reauth",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );

      if (!popup || popup.closed) {
        reject(new Error("popup_blocked"));
        return;
      }

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === "cobutler-reauth-success") {
          window.removeEventListener("message", handleMessage);
          clearInterval(pollTimer);
          resolve(event.data.payload as ReauthResult);
        } else if (event.data?.type === "cobutler-reauth-error") {
          window.removeEventListener("message", handleMessage);
          clearInterval(pollTimer);
          reject(new Error(event.data.error || "OAuth failed"));
        }
      };

      window.addEventListener("message", handleMessage);

      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          window.removeEventListener("message", handleMessage);
          reject(new Error("popup_closed"));
        }
      }, 500);
    } catch (error) {
      reject(error);
    }
  });
};

export const useAuthContext = () => useContext(AuthContext);
